import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TableSortLabel,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { CompanyIdentity, companyCellSx } from "../../shared/CompanyIdentity";
import { visuallyHidden } from "@mui/utils";
import dayjs from "dayjs";
import { DividendPayment, PaginationInfo } from "../Charts/hooks/useDividendAnalysis";

interface Column {
  id: keyof DividendPayment | 'company';
  label: string;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
  format?: (value: any, payment: DividendPayment, currencySymbol?: string) => React.ReactNode;
}

const getColumns = (currencySymbol: string): Column[] => [
  {
    id: 'company',
    label: 'Company',
    sortable: true,
    align: 'left',
    format: (_, payment) => {
      const cash = payment.ticker === "CASH" || payment.type === "interest";
      let leading: React.ReactNode;
      if (cash)
        leading = (
          <Box
            sx={{
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.main",
              bgcolor: "action.hover",
              borderRadius: 1,
            }}
          >
            {currencySymbol}
          </Box>
        );
      return (
        <CompanyIdentity
          company={payment.companyName}
          symbol={payment.ticker}
          image={payment.logo}
          linked={!cash}
          leading={leading}
        />
      );
    },
  },
  {
    id: 'amount',
    label: 'Amount',
    sortable: true,
    align: 'right',
    format: (value) => (
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {currencySymbol}{Number(value).toFixed(2)}
      </Typography>
    ),
  },
  {
    id: 'paymentDate',
    label: 'Payment Date',
    sortable: true,
    align: 'right',
    format: (value) => (
      <Typography variant="body2">
        {dayjs(value).format("MMM DD, YYYY")}
      </Typography>
    ),
  },
];

interface DividendPaymentsTableProps {
  payments: DividendPayment[];
  pagination?: PaginationInfo;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  isLoading?: boolean;
}

const DividendPaymentsTable: React.FC<DividendPaymentsTableProps> = ({
  payments,
  pagination,
  sortBy,
  sortOrder,
  onSort,
  onPageChange,
  onLimitChange,
  isLoading = false,
}) => {
  const currencySymbol = "$";
  const columns = getColumns(currencySymbol);
  
  const handleSort = (columnId: string) => {
    // Map company column to companyName for API
    const apiField = columnId === 'company' ? 'companyName' : columnId;
    onSort(apiField);
  };

  const getSortDirection = (columnId: string): 'asc' | 'desc' | false => {
    const apiField = columnId === 'company' ? 'companyName' : columnId;
    if (sortBy === apiField) {
      return sortOrder;
    }
    return false;
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
              : "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
          border: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.05)",
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.02)",
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      borderBottom: (theme) =>
                        theme.palette.mode === "dark"
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid rgba(0,0,0,0.08)",
                      py: 1.5,
                    }}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={getSortDirection(column.id) !== false}
                        direction={getSortDirection(column.id) || 'desc'}
                        onClick={() => handleSort(column.id)}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important',
                          },
                        }}
                      >
                        {column.label}
                        {getSortDirection(column.id) !== false ? (
                          <Box component="span" sx={visuallyHidden}>
                            {getSortDirection(column.id) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      {isLoading ? 'Loading payments...' : 'No dividend payments found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment, index) => (
                  <TableRow
                    key={payment.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.04)",
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          ...(column.id === 'company' ? { ...companyCellSx, width: '28%' } : {}),
                          borderBottom: (theme) =>
                            theme.palette.mode === "dark"
                              ? "1px solid rgba(255,255,255,0.05)"
                              : "1px solid rgba(0,0,0,0.05)",
                          py: 1,
                        }}
                      >
                        {column.format 
                          ? column.format(payment[column.id as keyof DividendPayment], payment)
                          : payment[column.id as keyof DividendPayment]
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination && (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {pagination.totalPayments > 0 
                ? ((pagination.currentPage - 1) * pagination.limit + 1)
                : 0} - {Math.min(
                  pagination.currentPage * pagination.limit,
                  pagination.totalPayments
                )} of {pagination.totalPayments} payments
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pagination.limit}
                  onChange={(e) => onLimitChange(Number(e.target.value))}
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0.5,
                      fontSize: '0.875rem',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.2)",
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              
              <Typography variant="body2" color="text.secondary">
                per page
              </Typography>

              {pagination.totalPages > 1 && (
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={(_, page) => onPageChange(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                  size="small"
                  sx={{
                    ml: 1,
                    '& .MuiPagination-ul': {
                      flexWrap: 'nowrap',
                    },
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2,
                      fontWeight: 500,
                      '&.Mui-selected': {
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "linear-gradient(135deg, rgba(34, 197, 94, 0.8) 0%, rgba(22, 163, 74, 0.7) 100%)"
                            : "linear-gradient(135deg, rgba(22, 163, 74, 0.9) 0%, rgba(34, 197, 94, 0.8) 100%)",
                        color: "#ffffff",
                      },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DividendPaymentsTable;